/**
 * The entity types that support like/watchlist, and the exact path segment each
 * one uses in `POST /like/{segment}/{id}` and `POST /watchlist/{segment}/{id}`.
 *
 * Keeping the segment in a map (instead of interpolating the app-side key
 * directly) means the backend's identifier spelling is defined in exactly one
 * place: if the API ever moves to different names, this map is the only edit.
 *
 * Every value below was probed against the configured backend
 * (NEXT_PUBLIC_API_BASE_URL = https://nftbaz.com/proxy-api/api). An
 * unauthenticated POST to a registered route answers 401 "Unauthenticated.";
 * an unregistered one answers 404 "The route api/... could not be found.".
 * Results — singular is registered, plural is not:
 *
 *   /like/collection/1  401      /like/collections/1  404
 *   /like/nft/1         401      /like/nfts/1         404
 *   /like/auction/1     401      /like/auctions/1     404
 *   /like/blog/1        401      /like/blogs/1        404
 *   /like/gallery/1     401      /like/galleries/1    404
 *   /like/drop/1        401      /like/drops/1        404
 *
 * `watchlist` mirrors `like` exactly for all six.
 *
 * `artist` is intentionally absent. Artists support **follow only** — there is
 * no like and no watchlist for them, on the backend or in the product. Every
 * spelling was probed and 404s (/like/artist/{id}, /like/artists/{id},
 * /artists/{id}/like, /like/user/{id}, /like/profile/{id}), the /artists
 * payload carries no `current_user` or `likes`, and GET /artists/watchlist 404s
 * too. Artist follow lives on its own route and hook and is unrelated to this
 * map: GET|POST|DELETE /artists/{username}/follow via
 * components/shared/FollowButton.tsx.
 */
export type InteractionModel =
  | "nft"
  | "collection"
  | "blog"
  | "gallery"
  | "auction"
  | "drop";

export const MODEL_PATH: Record<InteractionModel, string> = {
  nft: "nft",
  collection: "collection",
  blog: "blog",
  gallery: "gallery",
  auction: "auction",
  drop: "drop",
};

/**
 * First-segment fragments of the React Query keys whose cached payloads can
 * contain each entity type, used to scope the post-mutation cache patch in
 * ./cache.ts. Matched as substrings against `String(queryKey[0])`, because the
 * app's keys are not uniformly prefixed — collections alone appear under
 * "collections", "collections-endpoint", "category-detail" and
 * "artist-collections".
 *
 * The fragments are chosen so no two model types can ever match the same key,
 * which is what makes an id-based patch safe (an nft and a blog can both be
 * id 5, but they never share a query).
 */
export const MODEL_QUERY_FRAGMENTS: Record<InteractionModel, string[]> = {
  collection: ["collection", "category-detail"],
  nft: ["nft"],
  auction: ["auction"],
  blog: ["blog", "article"],
  gallery: ["galler"],
  drop: ["drop"],
};
